import { z } from 'zod';
import { BullMQPayloadBaseSchema, JobRedisStateBaseSchema } from './base.js';

/**
 * renumber-extract — extraction de la numérotation des pièces depuis un acte
 * juridique, scopée au dossier (case_id) plutôt qu'à un bordereau.
 *
 * Cas d'usage : « Numéroter mes pièces » (TICKET-258). L'utilisateur ouvre le
 * panel depuis l'espace de travail du dossier, fournit un acte (soit un
 * document existant du dossier, soit un upload ad-hoc — déjà persisté via
 * /api/storage/documents/upload), et le processor extrait les références
 * « Pièce n°X » + match avec les `case_documents` de catégorie nos_pieces.
 *
 * Aucune écriture DB côté processor : le résultat (extracted_pieces +
 * unmatched_documents) revient via Redis. L'application finale des
 * piece_number passe par `POST /api/cases/:caseId/pieces/renumber`
 * (endpoint sync, bulk UPDATE de case_documents.piece_number).
 *
 * `.passthrough()` pendant la fenêtre DEBT-069 (cf. workers/CLAUDE.md A3).
 */

/**
 * Source de l'extraction.
 * - `document` : pièce/document déjà persisté dans `case_documents` du dossier.
 * - `upload`   : fichier ad-hoc déjà uploadé via /api/storage/documents/upload
 *                (chemin OVH retourné par l'endpoint d'upload — non persisté
 *                dans case_documents).
 */
export const RenumberExtractSourceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('document'),
    document_id: z.string().uuid(),
  }),
  z.object({
    kind: z.literal('upload'),
    storage_path: z.string().min(1),
    mime_type: z.string().min(1),
  }),
]);
export type RenumberExtractSource = z.infer<typeof RenumberExtractSourceSchema>;

/**
 * Payload BullMQ enqueué par le route handler.
 * `documents` est pré-chargé par le route handler depuis `case_documents`
 * (catégorie nos_pieces) pour éviter une 2e DB round-trip côté processor.
 */
export const BullMQRenumberExtractPayloadSchema = BullMQPayloadBaseSchema.extend({
  case_id: z.string().uuid(),
  source: RenumberExtractSourceSchema,
  documents: z.array(
    z
      .object({
        document_id: z.string().uuid(),
        name: z.string(),
        category: z.string().optional(),
        piece_description: z.string().nullable().optional(),
        piece_number: z.string().nullable().optional(),
      })
      .passthrough(),
  ),
}).passthrough();
export type BullMQRenumberExtractPayload = z.infer<typeof BullMQRenumberExtractPayloadSchema>;

/**
 * Etat Redis du job. Le processor écrit le résultat dans `result` après
 * extraction réussie. Le frontend poll `GET /api/cases/:caseId/extract-piece-numbers/:jobId`
 * et lit `status` + `result`.
 */
export const RenumberExtractResultSchema = z
  .object({
    extracted_pieces: z.array(
      z
        .object({
          number: z.string(),
          label: z.string(),
          context: z.string().optional(),
          matched_document_id: z.string().uuid().nullable().optional(),
          confidence: z.number().optional(),
        })
        .passthrough(),
    ),
    unmatched_documents: z.array(
      z
        .object({
          document_id: z.string().uuid(),
          name: z.string(),
        })
        .passthrough(),
    ),
    stats: z
      .object({
        total_pieces: z.number(),
        matched_count: z.number(),
      })
      .passthrough(),
  })
  .passthrough();
export type RenumberExtractResult = z.infer<typeof RenumberExtractResultSchema>;

export const RenumberExtractJobDataSchema = JobRedisStateBaseSchema.extend({
  case_id: z.string().uuid(),
  result: RenumberExtractResultSchema.optional(),
}).passthrough();
export type RenumberExtractJobData = z.infer<typeof RenumberExtractJobDataSchema>;

/**
 * Payload de l'endpoint sync `POST /api/cases/:caseId/pieces/renumber`.
 * Chaque mapping est appliqué en bulk UPDATE sur case_documents.piece_number
 * (et propagé à case_document_embeddings.piece_number dans la même transaction).
 *
 * `piece_number` accepte `null` (efface le numéro) ou une string VARCHAR(50)
 * non-numérique (« 2bis », « Annexe A »). Pas de `0`/`'0'` valide côté UX.
 */
export const RenumberApplyMappingSchema = z.object({
  document_id: z.string().uuid(),
  piece_number: z.string().max(50).nullable(),
  piece_label: z.string().max(255).nullable().optional(),
});
export type RenumberApplyMapping = z.infer<typeof RenumberApplyMappingSchema>;

export const RenumberApplyRequestSchema = z.object({
  mappings: z.array(RenumberApplyMappingSchema).min(1).max(500),
});
export type RenumberApplyRequest = z.infer<typeof RenumberApplyRequestSchema>;
